import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Field, Input, Label, Paragraph, Select, Tag } from '@digdir/designsystemet-react';
import classes from '../CreateCustomSurveyTemplatePage.module.css';

const NEW = '__new';

type CategorySelectProps = {
  id: string;
  value: string;
  categories: string[];
  description?: string;
  onChange: (category: string) => void;
  onAdd: (category: string) => void;
};

/** Velg en kategori som finnes, eller lag en ny */
export const CategorySelect = ({ id, value, categories, description, onChange, onAdd }: CategorySelectProps) => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const descriptionId = description ? `${id}-description` : undefined;

  const add = () => {
    const category = draft.trim();
    if (!category) return;
    onAdd(category);
    onChange(category);
    setAdding(false);
    setDraft('');
  };

  return (
    <div className={classes.categoryField}>
      <Field>
        <Label htmlFor={id} className={classes.optionalLabel}>
          {t('customSurveys.create.category')}
          <Tag data-size="sm" data-color="neutral">
            {t('customSurveys.create.optional')}
          </Tag>
        </Label>
        {description && (
          <Paragraph data-size="sm" id={descriptionId} className={classes.subtle}>
            {description}
          </Paragraph>
        )}
        <Select
          id={id}
          value={adding ? NEW : value}
          aria-describedby={descriptionId}
          onChange={(event) => {
            const next = event.target.value;
            setAdding(next === NEW);
            if (next !== NEW) onChange(next);
          }}
        >
          <Select.Option value="">{t('customSurveys.create.noCategory')}</Select.Option>
          {categories.map((category) => (
            <Select.Option key={category} value={category}>
              {category}
            </Select.Option>
          ))}
          <Select.Option value={NEW}>{t('customSurveys.create.newCategory')}</Select.Option>
        </Select>
      </Field>
      {adding && (
        <div className={classes.newCategory}>
          <Field className={classes.grow}>
            <Label htmlFor={`${id}-new`} data-size="sm">
              {t('customSurveys.create.newCategoryName')}
            </Label>
            <Input
              id={`${id}-new`}
              value={draft}
              autoFocus
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  add();
                }
              }}
            />
          </Field>
          <Button variant="secondary" onClick={add}>
            {t('customSurveys.create.addCategory')}
          </Button>
        </div>
      )}
    </div>
  );
};
